import { Box, Typography, Button } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import Heart from "components/icons/Heart";

function StatCard({ label, value, unit, subtitle, color }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 0, md: 2 },
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#667085", fontWeight: 600 }}>
        {label}
      </Typography>
      {value ? (
        <Box
          sx={{ display: "flex", gap: "4px", alignItems: "baseline", color }}
        >
          <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{unit}</Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 24, fontWeight: 600, color: "#667085" }}>
          No Data
        </Typography>
      )}
      <Typography sx={{ color: "#667085", fontSize: 12, whiteSpace: "nowrap" }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unit: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  color: PropTypes.string,
};
StatCard.defaultProps = {
  value: null,
  color: "",
};

function getHeartRateStatus(bpm) {
  if (!bpm) return null;

  if (bpm >= 60 && bpm <= 100) {
    return {
      label: "Resting (60-100 BPM)",
      color: "#00A656",
      bgColor: "#00A6560D",
      borderColor: "#00A65626",
    };
  }
  if (bpm > 100 && bpm <= 130) {
    return {
      label: "Light Activity (100-130 BPM)",
      color: "#1F79E2",
      bgColor: "#1F79E20D",
      borderColor: "#1F79E226",
    };
  }
  if (bpm > 130 && bpm <= 160) {
    return {
      label: "Moderate (130-160 BPM)",
      color: "#FF9500",
      bgColor: "#FF95000D",
      borderColor: "#FF950026",
    };
  }
  if (bpm > 160) {
    return {
      label: "Vigorous (160+ BPM)",
      color: "#D32F2F",
      bgColor: "#A600000D",
      borderColor: "#A6000326",
    };
  }
  return {
    label: "Unknown",
    color: "#667085",
    bgColor: "#6670850D",
    borderColor: "#66708526",
  };
}

export default function HeartRateCard({
  lastMeasures,
  onSyncMeasure,
  isLoading,
  isSyncing,
  onUnsyncMeasure,
}) {
  const heartRate = lastMeasures?.hr_statistics;
  const lastSync = heartRate?.last_sync
    ? new Date(heartRate.last_sync).toLocaleString()
    : "-";

  const currentBpm = heartRate?.current;
  const heartRateStatus = getHeartRateStatus(currentBpm);

  const statistics = [
    {
      label: "Current",
      value: heartRate?.current,
      unit: "BPM",
      subtitle: `Last sync: ${lastSync}`,
    },
    {
      label: "Minimum",
      value: heartRate?.minimum,
      unit: "BPM",
      subtitle: "Resting baseline",
      color: "#FF1F56",
    },
    {
      label: "Maximum",
      value: heartRate?.maximum,
      unit: "BPM",
      subtitle: "Peak exertion",
      color: "#4280BE",
    },
  ];

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        p: 2.5,
        borderRadius: 2,
        backgroundColor: "#FFF",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            sx={{ fontWeight: 500, fontSize: 14, color: "#000000DE" }}
          >
            Heart Rate
          </Typography>
          {heartRateStatus && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: heartRateStatus.bgColor,
                color: heartRateStatus.color,
                border: `1px solid ${heartRateStatus.borderColor}`,
                borderRadius: "8px",
                fontWeight: 500,
                fontSize: 12,
                p: "7px 8px",
              }}
            >
              <Box
                sx={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  backgroundColor: heartRateStatus.color,
                }}
              />
              {heartRateStatus.label}
            </Box>
          )}
        </Box>
        <Button
          variant="contained"
          onClick={isSyncing ? onUnsyncMeasure : onSyncMeasure}
          disabled={isLoading}
          sx={{ textTransform: "uppercase", fontSize: 12, fontWeight: 600 }}
          startIcon={<Heart />}
        >
          {isSyncing ? "Stop Sync" : "Sync Measure"}
        </Button>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
          gap: 2.5,
          mb: 3,
        }}
      >
        <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
          <StatCard {...statistics[0]} />
        </Box>
        <StatCard {...statistics[1]} />
        <StatCard {...statistics[2]} />
      </Box>
    </Box>
  );
}

HeartRateCard.propTypes = {
  lastMeasures: PropTypes.shape({
    hr_statistics: PropTypes.shape({
      current: PropTypes.number,
      minimum: PropTypes.number,
      maximum: PropTypes.number,
      last_sync: PropTypes.string,
    }),
  }),
  onSyncMeasure: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isSyncing: PropTypes.bool,
  onUnsyncMeasure: PropTypes.func.isRequired,
};

HeartRateCard.defaultProps = {
  lastMeasures: {
    hr_statistics: {
      current: null,
      minimum: null,
      maximum: null,
      last_sync: null,
    },
  },
  isLoading: false,
  isSyncing: false,
};
